import { Injectable, Inject, ElementRef } from '@angular/core';
import { DOCUMENT } from '@angular/common';
import { Printer } from '@capgo/capacitor-printer';
import { Capacitor } from '@capacitor/core';
import { finalize, from, take } from 'rxjs';
import moment from 'moment-timezone';

const PRINT_TIMEOUT = 500;

/**
 * Selectors that must never reach the printed output: screen-only markup, plus the
 * icons, which are decorative status glyphs that only add noise on paper. They are
 * stripped from the clone instead of hidden with CSS, because the native print
 * formatter renders the markup on its own and cannot be trusted to apply the
 * stylesheet.
 */
const NON_PRINTABLE_SELECTOR =
  '.hide-on-print, .d-print-none, .d-sm-none, tr-icon, svg-icon, svg, img';

/**
 * Styles for the standalone document handed to the native printer. The document has
 * no app CSS at all, so this only needs the utility classes used inside printable
 * tables — not the ion-page/ion-content overrides the browser print flow relies on.
 */
const NATIVE_PRINT_STYLES = `
  body {
    margin: 0;
    color: #000;
    font-family: "Source Sans Pro", -apple-system, "Helvetica Neue", Arial, sans-serif;
    font-size: 12px;
  }

  table {
    width: 100%;
    margin: 0;
    border-collapse: collapse;
  }

  thead { display: table-header-group; }
  tbody { display: table-row-group; }
  tr { page-break-inside: avoid; }

  th, td {
    padding: 4px 6px;
    text-align: left;
    vertical-align: top;
    width: auto !important;
    min-width: 0 !important;
    max-width: none !important;
  }

  thead > tr > th {
    text-align: left;
    border-bottom: 1px solid #000;
  }

  td span:not(:last-child) { margin-right: 5px; }

  ul, .list-unstyled {
    margin: 0;
    padding-left: 0;
    list-style: none;
  }

  .hide-on-print, .d-print-none, .d-sm-none { display: none; }
  .d-print-block { display: block; }
  .d-print-inline { display: inline; }
  .d-print-inline-block { display: inline-block; }

  .d-flex { display: flex; }
  .d-block { display: block; }
  .flex-column { flex-direction: column; }
  .justify-content-center { justify-content: center; }
  .w-100 { width: 100%; }
  .print-minw-180 { min-width: 180px; }
  .py-2 {
    padding-top: .5rem;
    padding-bottom: .5rem;
  }
`;

@Injectable({ providedIn: 'root' })
export class PrintService {
  printFinished = true;
  private printSection: HTMLElement;

  constructor(@Inject(DOCUMENT) private readonly document: Document) {
    this.init();
  }

  init(): void {
    let printSection = this.document.getElementById('printSection');
    if (!printSection) {
      printSection = this.document.createElement('div');
      printSection.id = 'printSection';
      this.document.body.appendChild(printSection);
    }
    this.printSection = printSection;
  }

  print(elemToPrint: ElementRef): void {

    if (Capacitor.isNativePlatform()) {
      from(Printer.printHtml({
        html: this.buildNativeDocument(elemToPrint),
        name: moment().format('YYYY-MM-DD HH:mm'),
      }))
        .pipe(
          take(1),
          finalize(() => {
            this.printFinished = true;
          }),
        )
        .subscribe();
      return;
    }

    this.printSection.innerHTML = '';
    const domClone = elemToPrint.nativeElement.cloneNode(true) as HTMLElement;
    domClone.id = 'printClone';
    const appendResult: HTMLElement = this.printSection.appendChild(domClone);

    this.printFinished = false;
    window.print();

    window.onfocus = (): void => {
      setTimeout(() => {
        if (!this.printFinished) {
          appendResult.style.display = 'none';
          this.printFinished = true;
        }
      }, PRINT_TIMEOUT);
    };
  }

  /**
   * Builds a complete HTML document for the native printer. A bare fragment makes
   * iOS's UIMarkupTextPrintFormatter spill the <style> block into the output as
   * plain text instead of applying it, so head and body must be explicit.
   */
  private buildNativeDocument(elemToPrint: ElementRef): string {
    const clone = elemToPrint.nativeElement.cloneNode(true) as HTMLElement;
    clone.removeAttribute('id');
    clone.querySelectorAll(NON_PRINTABLE_SELECTOR).forEach((el) => {
      el.remove();
    });

    return `<!DOCTYPE html>
<html>
<head>
<meta charset="utf-8">
<style>${NATIVE_PRINT_STYLES}</style>
</head>
<body>${clone.outerHTML}</body>
</html>`;
  }
}
